import React from 'react'
import styled from 'styled-components'
import { AlertTriangle } from 'react-feather'
import { useTranslation } from 'react-i18next'
import { TokenTaxInfo, isVTToken } from '../../utils/taxDetection'
import { TYPE } from '../../theme'
import { AutoRow } from '../Row'
import { Token } from '@uniswap/sdk'

const WarningContainer = styled.div`
  background: rgba(243, 132, 30, 0.1);
  border: 1px solid #f3841e;
  border-radius: 12px;
  padding: 12px;
  margin: 8px 0;
`

const StyledWarningIcon = styled(AlertTriangle)`
  stroke: #f3841e;
  width: 16px;
  height: 16px;
`

interface TaxWarningProps {
  inputTaxInfo: TokenTaxInfo
  outputTaxInfo: TokenTaxInfo
  outputToken?: Token
}

export default function TaxWarning({ inputTaxInfo, outputTaxInfo, outputToken }: TaxWarningProps) {
  const { t } = useTranslation()

  // 如果输出代币是 VT，显示不支持购买的提示
  if (outputToken && isVTToken(outputToken.chainId, outputToken.address)) {
    return (
      <WarningContainer>
        <AutoRow gap="8px" align="center" style={{ flexWrap: 'nowrap' }}>
          <StyledWarningIcon style={{ flexShrink: 0 }} />
          <TYPE.main fontSize={14} color="#f3841e">
            {t('tokenNotAvailableForPurchase')}
          </TYPE.main>
        </AutoRow>
      </WarningContainer>
    )
  }

  // 检查是否有税费需要显示
  const hasTax = inputTaxInfo.hasTax || outputTaxInfo.hasTax
  if (!hasTax) {
    return null
  }

  // 检查是否两个代币都有税费
  const bothHaveTax = inputTaxInfo.hasTax && outputTaxInfo.hasTax

  if (bothHaveTax) {
    // 两个代币都有税费，计算总税费
    // 输入代币会被卖出，输出代币会被买入
    const inputSellTax = inputTaxInfo.sellTax || 0
    const outputBuyTax = outputTaxInfo.buyTax || 0
    const totalTax = inputSellTax + outputBuyTax

    // 如果总税费为0，不显示提示
    if (totalTax === 0) {
      return null
    }

    const recommendation = t('taxDetectedWithPercentage', {
      tax: totalTax.toFixed(1)
    })

    return (
      <WarningContainer>
        <AutoRow gap="8px" align="center" style={{ flexWrap: 'nowrap' }}>
          <StyledWarningIcon style={{ flexShrink: 0 }} />
          <TYPE.main fontSize={14} color="#f3841e">
            {recommendation}
          </TYPE.main>
        </AutoRow>
      </WarningContainer>
    )
  } else if (inputTaxInfo.hasTax) {
    // 只有输入代币有税费，这是卖出操作
    // 检查卖出税费是否为0
    if ((inputTaxInfo.sellTax || 0) === 0) {
      return null
    }

    const recommendation = t('taxDetectedSell', {
      sellTax: inputTaxInfo.sellTax?.toFixed(1)
    })
    return (
      <WarningContainer>
        <AutoRow gap="8px" align="center" style={{ flexWrap: 'nowrap' }}>
          <StyledWarningIcon style={{ flexShrink: 0 }} />
          <TYPE.main fontSize={14} color="#f3841e">
            {recommendation}
          </TYPE.main>
        </AutoRow>
      </WarningContainer>
    )
  } else if (outputTaxInfo.hasTax) {
    // 只有输出代币有税费，这是买入操作
    // 检查买入税费是否为0
    if ((outputTaxInfo.buyTax || 0) === 0) {
      return null
    }

    const recommendation = t('taxDetectedBuy', {
      buyTax: outputTaxInfo.buyTax?.toFixed(1)
    })
    return (
      <WarningContainer>
        <AutoRow gap="8px" align="center" style={{ flexWrap: 'nowrap' }}>
          <StyledWarningIcon style={{ flexShrink: 0 }} />
          <TYPE.main fontSize={14} color="#f3841e">
            {recommendation}
          </TYPE.main>
        </AutoRow>
      </WarningContainer>
    )
  }

  return null
}
